import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  IonButton,
  IonChip,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonPopover,
  IonRouterOutlet,
  IonSplitPane,
  IonTitle,
  IonToolbar,
  PopoverController,
} from '@ionic/angular/standalone';
import { MainHeaderComponent } from '../../components/main-header/main-header.component';
import { AppComponent } from '../../app.component';
import { NavController } from '@ionic/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { UserContextService } from '../../services/user-context.service';
import { Company, CompanyApi } from '../../apis/company.api';
import { isAdmin } from '../../apis/user.api';
import { TooltipPopoverComponent } from '../../components/tooltip-popover/tooltip-popover.component';
import EdroBrand from '../../services/ecosystems/brands/EdroBrand';
import { setBrandByOrganisation } from '../../services/ecosystems/ECoSystemsHelper';

export type MenuItem = {
  icon?: string;
  route?: string;
  tooltip?: string;
};

export type MainMenu = {
  primaryMenu: MenuItem[];
  secondaryMenu: MenuItem[];
};

@Component({
  selector: 'app-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  imports: [
    IonContent,
    CommonModule,
    FormsModule,
    IonRouterOutlet,
    IonMenu,
    IonList,
    IonItem,
    IonSplitPane,
    IonButton,
    IonLabel,
    TranslatePipe,
    IonPopover,
    IonChip,
  ],
})
export class HomePage implements OnInit, OnDestroy {
  private parent = inject(AppComponent);
  private userContext = inject(UserContextService);
  private route = inject(ActivatedRoute);
  translate = inject(TranslateService);
  companyApi = inject(CompanyApi);

  menuConfig: MainMenu = {
    primaryMenu: [],
    secondaryMenu: [],
  };

  public getCurrentUser() {
    return this.parent.currentUser;
  }

  public currentUserIsAdmin() {
    return (
      this.getCurrentUser()!.roles!.indexOf('ROLE_ADMIN') >= 0 ||
      this.getCurrentUser()!.roles!.indexOf('ROLE_SUPPORT') >= 0
    );
  }

  public getCompanies() {
    return this.userContext.getCompaniesData();
  }

  public hasAccessToCompanies(company: Company) {
    if (isAdmin(this.getCurrentUser())) {
      return true;
    }

    return this.getCompanies().some((c) => c.id == company.id);
  }

  /**
   * Référence au composant IonPopover.
   */
  @ViewChild('popover', { static: true }) popover!: IonPopover;

  @ViewChild(IonMenu) menu!: IonMenu;

  navController = inject(NavController);
  popoverController = inject(PopoverController);

  public popoverOpen: boolean = false;
  public currentOrganisationId: string | null = null;
  public currentOrganisation: Company | null = null;
  public isAdminSpace: boolean = false;

  constructor() {
    this.currentOrganisationId =
      this.route.snapshot.paramMap.get('organisationId');
    this.isAdminSpace = this.currentOrganisationId === 'admin';
  }

  public getBasePath() {
    return '/' + this.currentOrganisationId;
  }

  initMenu() {
    if (this.isAdminSpace) {
      this.menuConfig = {
        primaryMenu: [
          {
            route: this.getBasePath() + '/pools',
            icon: 'fa-regular fa-water-ladder',
            tooltip: 'pages.pools.list.title',
          },
          {
            route: this.getBasePath() + '/assistances',
            icon: 'fa-regular fa-comments',
            tooltip: 'pages.assistances.list.title',
          },
          {
            route: this.getBasePath() + '/estimates',
            icon: 'fa-regular fa-shovel',
            tooltip: 'pages.estimates.list.title',
          },
          {
            route: this.getBasePath() + '/firmwares',
            icon: 'fa-regular fa-upload',
            tooltip: 'pages.firmwares.list.title',
          },
        ],
        secondaryMenu: [
          {
            route: this.getBasePath() + '/users',
            icon: 'fa-regular fa-users',
            tooltip: 'pages.users.list.title',
          },
          {
            route: this.getBasePath() + '/companies',
            icon: 'fa-regular fa-buildings',
            tooltip: 'pages.companies.list.title',
          },
        ],
      };
    } else {
      this.menuConfig = {
        primaryMenu: [
          {
            route: this.getBasePath() + '/pools',
            icon: 'fa-regular fa-water-ladder',
            tooltip: 'pages.pools.list.title',
          },
        ],
        secondaryMenu: [],
      };

      if (
        isAdmin(this.getCurrentUser()) ||
        this.currentOrganisation?.currentRelation?.rank == 'admin'
      ) {
        this.menuConfig.secondaryMenu.push({
          route: this.getBasePath() + '/access',
          icon: 'fa-regular fa-users',
          tooltip: 'pages.companiesAccess.list.title',
        });
      }

      if (
        this.currentOrganisation &&
        this.currentOrganisation!.allowDelegateAssistance
      ) {
        this.menuConfig.primaryMenu.push({
          route: this.getBasePath() + '/assistances',
          icon: 'fa-regular fa-comments',
          tooltip: 'pages.assistances.list.title',
        });
      }

      if (
        this.currentOrganisation &&
        this.currentOrganisation!.allowSubCompany &&
        !this.currentOrganisation!.companyParentId
      ) {
        this.menuConfig.primaryMenu.push({
          route: this.getBasePath() + '/companies',
          icon: 'fa-regular fa-buildings',
          tooltip: 'pages.companies.list.title',
        });
      }

      this.menuConfig.secondaryMenu.push({
        route: this.getBasePath() + '/my-company',
        icon: 'fa-regular fa-building',
        tooltip: 'pages.mycompany.list.title',
      });
    }
  }

  reloadCurrentOrganisation() {
    if (!this.isAdminSpace) {
      this.companyApi.getCompany(this.currentOrganisationId!).then((org) => {
        this.currentOrganisation = org;
        setBrandByOrganisation(this.currentOrganisation);
        this.initMenu();
      });
    } else {
      setBrandByOrganisation(null);
      this.currentOrganisation = null;
      this.initMenu();
    }
  }

  ngOnInit() {
    this.reloadCurrentOrganisation();
  }

  ngOnDestroy() {
    this.onMenuMouseLeave({});
  }

  logout() {
    this.popover.dismiss().finally(() => {
      this.userContext.clear();
      this.navController.navigateRoot(['/auth', 'login']);
    });
  }

  async dismissPopover() {
    if (this.popover) {
      await this.popover.dismiss();
    }
  }

  /**
   * Ouvre le popover
   * @param event L'événement déclencheur.
   */
  openPopover(event: Event): void {
    // Logique pour ouvrir le popover si nécessaire.
    if (this.popover) {
      this.popover.event = event; // Nécessaire pour positionner correctement le popover.
      this.popover.present();
    }
  }

  popOverState(state: boolean) {
    this.popoverOpen = state;
  }

  async dismissAll() {
    await this.dismissPopover();
    await this.closeMenu();
  }

  private tooltipStack: any[] = [];
  private tooltipTimeout: any = null;

  async onMenuMouseEnter(event: any, text: string) {
    // Annuler tout timeout en cours
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }

    // Fermer tous les tooltips existants dans la stack
    await this.closeAllTooltips();

    // Créer le nouveau tooltip avec un léger délai
    this.tooltipTimeout = setTimeout(async () => {
      try {
        const tooltip = await this.popoverController.create({
          component: TooltipPopoverComponent,
          componentProps: {
            text: text,
          },
          cssClass: ['menu-tooltip'],
          side: 'right',
          alignment: 'start',
          arrow: false,
          showBackdrop: false,
          animated: false,
          mode: 'ios',
          event: event,
        });

        // Ajouter le tooltip à la stack avant de le présenter
        this.tooltipStack.push(tooltip);
        await tooltip.present();
      } catch (error) {
        console.warn('Erreur lors de la création du tooltip:', error);
      }
    }, 200); // Délai de 200ms pour éviter les tooltips fugaces
  }

  async onMenuMouseLeave(event: any) {
    // Annuler la création du tooltip si elle est en cours
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }

    // Fermer tous les tooltips avec un léger délai
    setTimeout(async () => {
      await this.closeAllTooltips();
    }, 100);
  }

  /**
   * Ferme tous les tooltips dans la stack
   */
  private async closeAllTooltips() {
    const tooltipsToClose = [...this.tooltipStack]; // Copie de la stack
    this.tooltipStack = []; // Vider la stack immédiatement

    // Fermer tous les tooltips en parallèle
    const closePromises = tooltipsToClose.map(async (tooltip) => {
      try {
        if (tooltip) {
          await tooltip.dismiss();
        }
      } catch (error) {
        console.warn("Erreur lors de la fermeture d'un tooltip:", error);
      }
    });

    // Attendre que tous les tooltips soient fermés
    await Promise.allSettled(closePromises);
  }

  /**
   * Force la fermeture de tous les tooltips et annule les timeouts
   */
  private async forceCloseAllTooltips() {
    // Annuler tout timeout en cours
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }

    // Fermer tous les tooltips
    await this.closeAllTooltips();
  }
  async goTo(uri: string) {
    this.navController.navigateRoot(uri);
    await this.dismissAll();
  }

  goToEntrepriseSpace(company: Company) {
    this.navController.navigateRoot('/' + company.id);
  }

  returnToAdmin() {
    this.navController.navigateRoot('/admin');
  }

  async goToProfil() {
    this.navController.navigateRoot(
      this.getBasePath() + '/' + this.getCurrentUser()!.id + '/account',
    );

    await this.dismissAll();
  }

  async closeMenu() {
    await this.menu.close();
  }

  uriContains(uri: string) {
    return document.location.href.includes(uri);
  }

  protected readonly isAdmin = isAdmin;
}
